import React, { useState, useEffect } from 'react';
import { 
  Menu, X, ChevronDown, ChevronRight, FileText, BookOpen, 
  Video, CheckCircle2, Lock, PlayCircle, Download, FileSpreadsheet,
  RotateCcw, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const modules = [
  {
    id: 1,
    title: 'NIL Return',
    subtitle: 'Zero outward supplies filing',
    icon: FileText,
    filingInstructions: `
## How to File NIL GSTR-1 Return

### When to File NIL Return?
File a NIL return when you have **NO outward supplies** during the tax period. This includes:
- No B2B sales (to registered businesses)
- No B2C sales (to consumers)
- No exports
- No advances received
- No credit/debit notes issued

### Step-by-Step Filing Process:

**Step 1:** Login to GST Portal with your credentials

**Step 2:** Navigate to Returns → File Returns → Select Period

**Step 3:** Click "PREPARE ONLINE" on GSTR-1 card

**Step 4:** Check the ☑️ **"File Nil GSTR-1"** checkbox at the top

**Step 5:** Read the green Note box that appears with conditions

**Step 6:** Click **"FILE STATEMENT"** button

**Step 7:** On Filing page:
- Check the declaration checkbox
- Select your Authorised Signatory
- Click **"FILE WITH EVC"**

**Step 8:** Enter OTP (123456 for simulation) and verify

**Step 9:** Congratulations! Your NIL Return is filed. Save the ARN number.

### Important Notes:
- NIL return means zero tax liability
- Due date is 11th of following month
- Late filing attracts penalty
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'No invoices required', available: false },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  },
  {
    id: 2,
    title: 'B2B Invoices',
    subtitle: '4A, 4B, 6B, 6C - Registered persons',
    icon: FileSpreadsheet,
    filingInstructions: `
## How to File B2B Invoices in GSTR-1

### What are B2B Invoices?
B2B (Business to Business) invoices are for supplies made to **registered taxpayers** (those having GSTIN).

### When to Report B2B Invoices?
- All sales to registered businesses
- SEZ supplies with payment of tax
- Deemed exports

### Required Details:
- Receiver's GSTIN
- Invoice Number & Date
- Invoice Value
- Taxable Value
- Tax Rate & Amount (IGST/CGST/SGST)
- Place of Supply

### Step-by-Step Process:
1. Click on "4A, 4B, 6B, 6C - B2B Invoices" tile
2. Click "ADD RECORD"
3. Fill invoice details
4. Save and add more if needed
5. Generate Summary when done

*Module coming soon with practice exercises*
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'Sample Invoice 1 (Interstate - IGST)', available: true, downloadUrl: '/api/b2b/sample-invoice/1' },
      { type: 'invoice', title: 'Sample Invoice 2 (Intrastate - CGST/SGST)', available: true, downloadUrl: '/api/b2b/sample-invoice/2' },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  },
  {
    id: 3,
    title: 'B2C (Large) Invoices',
    subtitle: 'Inter-state supplies > ₹2.5 Lakh',
    icon: FileSpreadsheet,
    filingInstructions: `
## How to File B2C (Large) Invoices

### What are B2C Large Invoices?
B2C Large invoices are for **inter-state** supplies to **unregistered persons** where invoice value exceeds **₹2.5 Lakhs**.

### Key Points:
- Only inter-state supplies
- Invoice value > ₹2.5 Lakhs
- Consumer doesn't have GSTIN
- Invoice-wise reporting required

### Required Details:
- Place of Supply (State)
- Invoice Number & Date
- Invoice Value
- Tax Rate & Amount

*Module coming soon with practice exercises*
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'Sample B2C Large Invoice', available: true },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  },
  {
    id: 4,
    title: 'B2C (Others)',
    subtitle: 'Intra-state & small inter-state',
    icon: FileSpreadsheet,
    filingInstructions: `
## How to File B2C (Others) Invoices

### What are B2C Others?
This covers all B2C supplies NOT covered under B2C Large:
- All intra-state B2C supplies
- Inter-state B2C supplies ≤ ₹2.5 Lakhs

### Reporting:
- Consolidated state-wise reporting
- No invoice-wise details needed
- Report total taxable value per rate

*Module coming soon with practice exercises*
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'Sample B2C Invoice', available: true },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  },
  {
    id: 5,
    title: 'Export Invoices',
    subtitle: '6A - Zero-rated supplies',
    icon: FileSpreadsheet,
    filingInstructions: `
## How to File Export Invoices

### What are Export Invoices?
Exports are **zero-rated supplies** made to recipients outside India.

### Types of Exports:
1. **With Payment of IGST** - Claim refund later
2. **Under Bond/LUT** - No tax payment

### Required Details:
- Export Type
- Shipping Bill Number & Date
- Port Code
- Invoice details
- IGST amount (if applicable)

*Module coming soon with practice exercises*
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'Sample Export Invoice', available: true },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  },
  {
    id: 6,
    title: 'Credit/Debit Notes',
    subtitle: '9B - Amendments & corrections',
    icon: FileSpreadsheet,
    filingInstructions: `
## How to File Credit/Debit Notes

### What are Credit/Debit Notes?
- **Credit Note**: Issued when invoice value decreases (returns, discounts)
- **Debit Note**: Issued when invoice value increases

### When to Issue:
- Goods returned
- Post-sale discount
- Price revision
- Deficiency in services

### Required Details:
- Original Invoice reference
- Note Number & Date
- Reason for issuance
- Differential tax amount

*Module coming soon with practice exercises*
    `,
    resources: [
      { type: 'instruction', title: 'Filing Instructions', available: true },
      { type: 'invoice', title: 'Sample Credit Note', available: true },
      { type: 'video', title: 'Video Tutorial', available: false, comingSoon: true }
    ]
  }
];

const GSTR1ModuleSidebar = ({ isOpen, onToggle, activeModule, onSelectModule, onStartModule, moduleCompletions = {} }) => {
  const [expandedModule, setExpandedModule] = useState(null);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [selectedModuleInstructions, setSelectedModuleInstructions] = useState(null);
  
  // Practice count from localStorage
  const [practiceCounts, setPracticeCounts] = useState(() => {
    const saved = localStorage.getItem('gstr1_practice_counts');
    return saved ? JSON.parse(saved) : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  });

  // Save practice counts to localStorage
  useEffect(() => {
    localStorage.setItem('gstr1_practice_counts', JSON.stringify(practiceCounts));
  }, [practiceCounts]);
  
  // Get dynamic module status based on completions
  const getModuleStatus = (moduleId) => {
    if (moduleCompletions[moduleId]) return 'completed';
    return 'available';
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const handlePractice = (moduleId) => {
    // Increment practice count
    setPracticeCounts(prev => ({
      ...prev,
      [moduleId]: (prev[moduleId] || 0) + 1
    }));
    
    // Trigger the module start
    if (onStartModule) {
      onStartModule(moduleId);
    } else {
      onToggle(); // Fallback: Close sidebar
    }
  };

  const handleShowInstructions = (module) => {
    setSelectedModuleInstructions(module);
    setShowInstructionsModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'available':
        return <PlayCircle className="w-5 h-5 text-blue-500" />;
      case 'locked':
        return <Lock className="w-5 h-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'instruction':
        return <BookOpen className="w-4 h-4" />;
      case 'invoice':
        return <Download className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  // Calculate progress based on actual completions
  const completedModules = Object.values(moduleCompletions).filter(Boolean).length;
  const totalModules = modules.length;
  const progressPercent = Math.round((completedModules / totalModules) * 100 * 100) / 100; // 2 decimal places

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={onToggle}
        className="fixed top-20 left-4 z-50 p-2 bg-[#1e3a5f] text-white rounded-lg shadow-lg hover:bg-[#2a5298] transition-colors"
        data-testid="module-sidebar-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5298] text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold">GSTR-1 Modules</h2>
            <button onClick={onToggle} className="p-1 hover:bg-white/20 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-blue-200">Learn step-by-step with case studies</p>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Course Progress</span>
            <span className="font-semibold text-[#1e3a5f]">{progressPercent}% ({completedModules}/{totalModules})</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-gradient-to-r from-green-500 to-green-400 h-2.5 rounded-full transition-all duration-500" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Modules List */}
        <div className="overflow-y-auto h-[calc(100%-200px)]">
          {modules.map((module) => {
            const status = getModuleStatus(module.id);
            return (
            <div key={module.id} className="border-b">
              {/* Module Header */}
              <button
                onClick={() => {
                  toggleModuleExpand(module.id);
                  onSelectModule(module.id);
                }}
                className={`w-full px-6 py-4 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left ${
                  activeModule === module.id ? 'bg-blue-50 border-l-4 border-[#1e3a5f]' : ''
                }`}
              >
                <div className="mt-0.5">
                  {getStatusIcon(status)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400">MODULE {module.id}</span>
                    {status === 'completed' && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Done</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-800 mt-1">{module.title}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{module.subtitle}</p>
                  {/* Practice Count */}
                  {practiceCounts[module.id] > 0 && (
                    <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" />
                      Practiced {practiceCounts[module.id]} time{practiceCounts[module.id] > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                <div className="mt-2">
                  {expandedModule === module.id ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Resources (Expanded) */}
              {expandedModule === module.id && (
                <div className="bg-gray-50 px-6 py-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Resources
                  </p>
                  {module.resources.map((resource, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (resource.type === 'instruction' && resource.available) {
                          handleShowInstructions(module);
                        } else if (resource.type === 'invoice' && resource.available && resource.downloadUrl) {
                          // Download the invoice PDF
                          const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                          window.open(API_URL + resource.downloadUrl, '_blank');
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        resource.available 
                          ? 'bg-white hover:bg-blue-50 text-gray-700 border border-gray-200 cursor-pointer' 
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      disabled={!resource.available}
                    >
                      <span className={resource.available ? 'text-[#1e3a5f]' : 'text-gray-400'}>
                        {getResourceIcon(resource.type)}
                      </span>
                      <span className="flex-1 text-left">{resource.title}</span>
                      {resource.available && resource.type === 'instruction' && (
                        <Eye className="w-4 h-4 text-blue-500" />
                      )}
                      {resource.available && resource.type === 'invoice' && resource.downloadUrl && (
                        <Download className="w-4 h-4 text-green-500" />
                      )}
                      {resource.comingSoon && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </button>
                  ))}
                  
                  {/* Practice Button */}
                  {status === 'completed' ? (
                    <button 
                      onClick={() => handlePractice(module.id)}
                      className="w-full mt-3 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                      data-testid={`practice-module-${module.id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                      Practice Again
                    </button>
                  ) : (
                    <button 
                      onClick={() => handlePractice(module.id)}
                      className="w-full mt-3 py-2.5 bg-[#1e3a5f] hover:bg-[#2a5298] text-white rounded-lg text-sm font-semibold transition-colors"
                      data-testid={`start-module-${module.id}`}
                    >
                      Start Module
                    </button>
                  )}
                </div>
              )}
            </div>
          )})}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-100 border-t">
          <p className="text-xs text-gray-500 text-center">
            PractiQ - Learn by Doing
          </p>
        </div>
      </div>

      {/* Filing Instructions Modal */}
      <Dialog open={showInstructionsModal} onOpenChange={setShowInstructionsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1e3a5f] flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              {selectedModuleInstructions?.title} - Filing Instructions
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            {selectedModuleInstructions?.filingInstructions.split('\n').map((line, idx) => {
              if (line.startsWith('## ')) {
                return <h2 key={idx} className="text-lg font-bold text-[#1e3a5f] mt-4 mb-2">{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={idx} className="text-md font-semibold text-gray-800 mt-3 mb-1">{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('**Step')) {
                return <p key={idx} className="my-2 bg-blue-50 p-2 rounded border-l-4 border-blue-500">{line.replace(/\*\*/g, '')}</p>;
              } else if (line.startsWith('- ')) {
                return <li key={idx} className="ml-4 text-gray-700">{line.replace('- ', '')}</li>;
              } else if (line.startsWith('*') && line.endsWith('*')) {
                return <p key={idx} className="italic text-gray-500 mt-4">{line.replace(/\*/g, '')}</p>;
              } else if (line.trim()) {
                return <p key={idx} className="text-gray-700 my-1">{line}</p>;
              }
              return null;
            })}
          </div>
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowInstructionsModal(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setShowInstructionsModal(false);
                handlePractice(selectedModuleInstructions?.id);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Start Practice
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GSTR1ModuleSidebar;
